import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Search} from 'lucide-react';
import Sidebar from '../Sidebar';
import TeamsInterviewModal from './TeamsInterviewModal';
import CandidateDetailsSection from './CandidateDetailsSection';
import { useNavigate } from 'react-router-dom';

const IndividualCandidateDashboard = () => {
    const { candidateId } = useParams();
    const [candidateDetails, setCandidateDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();
     useEffect(() => {
        const checkAuth = () => {
            const adminToken = localStorage.getItem('adminToken');
            const adminUser = localStorage.getItem('adminUser');

            if (!adminToken || !adminUser) {
                navigate('/login', { replace: true });
                return false;
            }

            if (!adminToken.startsWith('admin_session_')) {
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                navigate('/login', { replace: true });
                return false;
            }

            try {
                const base64 = adminToken.replace('admin_session_', '');
                const decoded = atob(base64);
                const tokenData = JSON.parse(decoded);

                const now = Math.floor(Date.now() / 1000);
                if (tokenData.exp < now) {
                    localStorage.removeItem('adminToken');
                    localStorage.removeItem('adminUser');
                    navigate('/login', { replace: true });
                    return false;
                }

                return true;
            } catch (err) {
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                navigate('/login', { replace: true });
                return false;
            }
        };

        if (checkAuth() && candidateId) {
            fetchCandidateDetails(candidateId);
        }
    }, [navigate, candidateId]);

    const fetchCandidateDetails = async (id) => {
        setLoading(true);
        setError(null);
        try {
            //console.log('🔍 Fetching details for candidate:', id);
            const response = await fetch(`https://n8n.acespireconsulting.com/webhook/api/candidate/chatbot-score?candidateId=${id}`);
            
            //console.log('📥 Response status:', response.status);
            //console.log('📥 Response ok:', response.ok);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const contentType = response.headers.get('content-type') || '';
            const contentLength = response.headers.get('content-length') || '0';
            
            //console.log('📄 Content-Type:', contentType);
            //console.log('📏 Content-Length:', contentLength);
            
            const responseText = await response.text();
            //console.log('📄 Raw response text:', responseText);
           // console.log('📄 Response text length:', responseText.length);
            
            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }
            
            let data;
            try {
                data = JSON.parse(responseText);
               // console.log('📊 Parsed candidate details:', data);
            } catch (jsonError) {
                console.error('❌ JSON parsing failed:', jsonError);
                console.error('Response that failed to parse:', responseText);
                throw new Error(`Invalid JSON response: ${jsonError.message}`);
            }
            
            if (data && Array.isArray(data) && data.length > 0) {
                setCandidateDetails(data[0]);
            } else if (data && !Array.isArray(data)) {
                setCandidateDetails(data);
            } else {
                setError('Candidate not found or no data returned');
            }
        } catch (error) {
            console.error('❌ Error fetching candidate details:', error);
            
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                setError('Cannot connect to server. Please check if the API is running.');
            } else if (error.message.includes('JSON')) {
                setError('Server returned invalid data format. Please try again.');
            } else if (error.message.includes('Empty response')) {
                setError('Server returned empty response. The candidate may not exist.');
            } else {
                setError(error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleScheduleInterview = () => {
        setIsModalOpen(true);
    };

    const handleSendChatbotInvite = async () => {
        try {
           // console.log('🤖 Sending chatbot interview invite for candidate:', candidateId);
            
            const response = await fetch('https://n8n.acespireconsulting.com/webhook/send-interview-invite', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    candidateId: parseInt(candidateId),
                    triggeredBy: 'hr@company.com',
                    triggeredAt: new Date().toISOString()
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            //console.log('✅ Chatbot interview invite sent:', result);
            
            await fetchCandidateDetails(candidateId);
            
            alert(`Chatbot interview invite sent successfully to ${candidateDetails?.candidateName}!`);
        } catch (error) {
            console.error('❌ Error sending chatbot invite:', error);
            alert(`Failed to send chatbot interview invite: ${error.message}`);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleInterviewScheduled = () => {
        fetchCandidateDetails(candidateId);
        setIsModalOpen(false);
    };

    if (loading) {
        return (
            <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="flex-shrink-0 h-screen overflow-y-auto custom-scrollbar">
                    <Sidebar />
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-gray-600 text-xl">Loading candidate details...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="flex-shrink-0 h-screen overflow-y-auto custom-scrollbar">
                    <Sidebar />
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-red-600 text-xl mb-4">Error loading candidate details</div>
                        <div className="text-gray-600 mb-4">{error}</div>
                    </div>
                </div>
            </div>
        );
    }

    if (!candidateDetails) {
        return (
            <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="flex-shrink-0 h-screen overflow-y-auto custom-scrollbar">
                    <Sidebar />
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-gray-600 text-xl mb-4">Candidate not found</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-gray-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
             <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 4px;
                    border: 1px solid #f1f5f9;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
                .custom-scrollbar::-webkit-scrollbar-corner {
                    background: #f1f5f9;
                }
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: #cbd5e1 #f1f5f9;
                }
            `}</style>
            {/* Sidebar */}
            <div className="flex-shrink-0 h-screen overflow-y-auto custom-scrollbar">
                <Sidebar />
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top Header */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <h1 className="text-lg font-semibold text-gray-900">Hireon AI</h1>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search Filters"
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 w-64 text-sm"
                            />
                        </div>
                        <button className="px-4 py-2 bg-[#275F97] text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                            Save Filter
                        </button>
                    </div>
                </div>

                {/* Main Dashboard Content - Now Scrollable */}
                <div className="flex-1 overflow-y-auto p-4">
                    <CandidateDetailsSection 
                        candidateDetails={candidateDetails}
                        onScheduleInterview={handleScheduleInterview}
                        onSendChatbotInvite={handleSendChatbotInvite}
                    />
                </div>
            </div>

            {/* Teams Interview Modal */}
            <TeamsInterviewModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                candidateDetails={candidateDetails}
                onInterviewScheduled={handleInterviewScheduled}
            />
        </div>
    );
};

export default IndividualCandidateDashboard;